import React, { useEffect, useState } from 'react';
import TextInput from "../TextInput";
import { Button } from "../Button";
import Result from "../Result";
import './child-tax-credit.css';
import { formatNumber, parseNumber, postHeightToParent, toUsFormat, validInteger } from "../common-helpers";
import { calculate } from './helpers';

export const ChildTaxCredit: React.FC = () => {
	const [filingSatus, setFilingSatus] = useState("single");
	const [children, setChildren] = useState("0");
	const [childrenSixPlus, setChildrenSixPlus] = useState("0");
	const [monthlyPayment, setMonthlyPayment] = useState("0");
	const [creditAmount, setCreditAmount] = useState("0");
	const [income, setIncome] = useState("");
	const [hasIncomeError, setHasIncomeError] = useState(false);
	const [hasChildrenError, setHasChildrenError] = useState(false);
	const [hasChildrenSixPlusError, setHasChildrenSixPlusError] = useState(false);
	const [hasResult, setHasResult] = useState(false);

	const filingStatusOptions = [
			{label: "Single", value: "single"},
			{label: "Married Filing Jointly", value: "married-jointly"},
			{label: "Married Filing Separately", value: "married-separately"},
			{label: "Head-of-Household", value: "head-houshold"},
			{label: "Qualifying Widow(er)", value: "widower"},
	];

	const onIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue: string = parseNumber(e.target.value).toString();

    if (inputValue === '') { setHasIncomeError(true); }
		else if (hasIncomeError === true) { setHasIncomeError(false); }

    setIncome(inputValue);
  };

	const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilingSatus(e.currentTarget.value);
  };

	const onChildrenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (hasChildrenError === true) { setHasChildrenError(false); }
    setChildren(validInteger(e.target.value));
  };

	const onSixPlusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (hasChildrenSixPlusError === true) { setHasChildrenSixPlusError(false); }
    setChildrenSixPlus(validInteger(e.target.value));
  };

	const onSubmit = () => {
		if (income === "") { setHasIncomeError(true); }
		else if (children === "") { setHasChildrenError(true); }
		else if (childrenSixPlus === "") { setHasChildrenSixPlusError(true); }
		else {
			const incomeInt = Number(income);
			const { monthlyPayment, creditAmount } = calculate(
				filingSatus,
				Number(children),
				Number(childrenSixPlus),
				incomeInt
			);
			setMonthlyPayment(toUsFormat(monthlyPayment));
			setCreditAmount(toUsFormat(creditAmount));
			setHasResult(true);
		}
	};
	const onReset = () => {
		setFilingSatus("single");
		setChildren("0");
		setChildrenSixPlus("0");
		setIncome("");
		setHasResult(false);
	};

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [hasChildrenError, hasChildrenSixPlusError, hasIncomeError, hasResult]);

	return (
		<div className="container__childTaxCredit">
			<form className="childTaxCredit-form">
				<div className="inputGroup">
					<div className="childTaxCredit-step">Step 1</div>
					<div className="childTaxCredit-question">What filing status did you use on your 2020 tax return?</div>
					<div className="childTaxCredit-tip">Use your 2019 return if you didn't file a 2020 return.</div>
					<fieldset id="filing-status">
						{filingStatusOptions && filingStatusOptions.map((option, i) => {
							return (
								<label className="childTaxCredit-input-radio-label" key={`childTaxCredit-radio-input-${i}`}>
									<input
										type="radio"
										name="filing-status"
										value={option.value}
										checked={filingSatus === option.value}
										onChange={onRadioChange}
										/> {option.label}
								</label>
							)
						})}
					</fieldset>
				</div>

				<div className="inputGroup">
					<div className="childTaxCredit-step">Step 2</div>
					<label htmlFor="children-input" className="childTaxCredit-question">How many children age 5 and under did you have on December 31, 2021?</label>
					<input
						id="children-input"
						type="text"
						pattern="/\d+/g"
						inputMode="numeric"
						name="children"
						value={children}
						className={hasChildrenError ? `childTaxCredit-input childTaxCredit-input_error` : `childTaxCredit-input`}
						onChange={onChildrenChange}
						required
					/>
					{hasChildrenError && (
						<p className="childTaxCredit-error">Please, enter number of chilfren.</p>
					)}
				</div>

				<div className="inputGroup">
					<div className="childTaxCredit-step">Step 3</div>
					<label htmlFor="childrenSixPlus-input" className="childTaxCredit-question">How many children age 6 to 17 did you have on December 31, 2021?</label>
					<input
						id="childrenSixPlus-input"
						type="text"
						pattern="/\d+/g"
						inputMode="numeric"
						name="children-six-plus"
						value={childrenSixPlus}
						className={hasChildrenSixPlusError ? `childTaxCredit-input childTaxCredit-input_error` : `childTaxCredit-input`}
						onChange={onSixPlusChange}
						required
					/>
					{hasChildrenSixPlusError && (
						<p className="childTaxCredit-error">Please, enter number of children.</p>
					)}
				</div>

				<div className="inputGroup">
					<div className="childTaxCredit-step">Step 4</div>
					<label htmlFor="childTaxCredit-income-input" className="childTaxCredit-question">What's the adjusted gross income reported on your 2020 tax return?</label>
					<div className="childTaxCredit-tip">Use your 2019 return if you didn't file a 2020 return. Add any amount excluded from gross income on your tax return as foreign earned income; foreign housing expenses; or as income from sources within Puerto Rico, Guam, American Samoa or the Northern Mariana Islands.</div>
					<TextInput
						id="childTaxCredit-income-input"
						name="income"
						inputMode="numeric"
						fieldClass={hasIncomeError ? `childTaxCredit-input childTaxCredit-input_error` : `childTaxCredit-input`}
						onChange={onIncomeChange}
						value={formatNumber(income)}
						data-testid="childTaxCredit-income"
					/>
					{hasIncomeError && (
						<p className="childTaxCredit-error">Please, enter correct sum.</p>
					)}
				</div>

				{hasResult && (
					<div className="childTaxCredit-result_container">
						<div className="childTaxCredit-result">Your estimated monthly payments from July to December 2021 should have been:</div>
						<div className="childTaxCredit-tip">(Actual monthly payments may have been greater for people who got their first monthly payment after July, since the total advance payment amount was spread over less than six months.)</div>
						<Result
							id="childTaxCredit-monthly-payment"
							result={monthlyPayment}
							classNames="childTaxCredit-result-value"
						/>
						<div className="childTaxCredit-result">Your estimated credit amount to claim on your 2021 tax return (to be filed in 2022) is:</div>
						<Result
							id="childTaxCredit-credit-amount"
							result={creditAmount}
							classNames="childTaxCredit-result-value"
						/>
					</div>
				)}
				<Button
				label="Submit"
				className="primary-button childTaxCredit-btn"
				onClickHandler={onSubmit}
				/>
				<Button
					label="Clear Form"
					className="default-button childTaxCredit-btn"
					onClickHandler={onReset}
				/>
			</form>
		</div>
	);
}
